import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CardSectionNew } from 'shared/components/CardSectionNew';
import { SimilarOffersAnchor } from 'shared/components/SimilarOffersSection';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { CpdLabelContainer } from 'shared/containers/CpdLabelContainer';
import { CpdLabelModalsContainer } from 'shared/containers/CpdLabelModals';
import { CpdPrelandingModal } from 'shared/containers/CpdPrelandingModal';
import { CpdUnderDescriptionContainer } from 'shared/containers/CpdUnderDescriptionContainer';
import {
  EInfrastructureProfile,
  InfrastructureMicrofrontendContainer,
} from 'shared/containers/InfrastructureMicrofrontendContainer';
import { OnlineRentalAgreementLabelContainer } from 'shared/containers/OnlineRentalAgreementLabel';
import { SecondaryMortgageCalculatorContainer } from 'shared/containers/SecondaryMortgageCalculatorContainer';
import { HomeownerTrapContainer, ValuationTrapLoader } from 'shared/containers/valuationTraps';
import { useCardUxfeedbackCampaign } from 'shared/hooks/useCardUxfeedbackCampaign';
import { useGetEtazhiBlackFridayPriceData } from 'shared/hooks/useGetEtazhiBlackFridayPriceData';
import { prefetchFormState } from 'shared/store/deal-rent';
import { hasGetParameter } from 'shared/utils/hasGetParameter';

import { EarlyAccessPaywallContainer } from '../../../packages/EarlyAccessPaywall';
import { FeedbackPopupContainer as FeedBackPopupC } from '../../FeedbackExperiment/components/PopupWithComplaint/FeedbackPopup';
import { PopupFeedbackSentContainer } from '../../FeedbackExperiment/components/PopupWithForm/PopupFeedbackSent';
import { LabelsLayoutNew } from '../../components/LabelsLayout';
import {
  OfferCardPageLayout,
  OfferCardPageLayoutAside,
  OfferCardPageLayoutCenter,
  OfferCardPageLayoutFooter,
  OfferCardPageLayoutTop,
} from '../../components/OfferCardPageLayout';
import { OfferHistoryContainer } from '../../components/OfferHistory/containers';
import { AdminButtonsContainer } from '../../components/admin_buttons/container';
import { HeaderAdminPanelContainer } from '../../components/header_admin_panel/container';
import { OfferBreadcrumbsContainer } from '../../components/offer_breadcrumbs/container';
import { OfferGallery } from '../../components/offer_gallery';
import { OfferIsHiddenBlock } from '../../components/offer_hidden_block';
import { OfferRefusedContainer } from '../../components/offer_refused/container';
import { OfferUnpublishedContainer } from '../../components/offer_unpublished/container';
import { HideOfferTopPopup } from '../../components/popups/hide_offer_top_popup';
import { LegendModalContainer } from '../../components/popups/legend/container';
import { PrintModalContainer } from '../../components/popups/print/container';
import { RemoveModalContainer } from '../../components/popups/remove/container';
import { PrintInformationContainer } from '../../components/print_information/container';
import { PrintPhotoContainer } from '../../components/print_photo/container';
import { getOffer } from '../../selectors/newObject';
import { IState } from '../../store';
import { TgbBottomBannersContainer } from '../Adfox/TgbBottomBannersContainer';
import { AsideRedesignedContainer } from '../AsideRedesigned/AsideRedesigned';
import { AuctionLabelContainer } from '../AuctionLabel';
import { BottomAuthorBlockContainer } from '../BottomAuthorBlockNew';
import { CallUnderDescriptionContainer } from '../CallUnderDescriptionContainer';
import { ChatPopupContainer } from '../ChatPopupContainer';
import { CianLayoutContainer } from '../CianLayout';
import { ComparisonNotificationLoadableContainer } from '../ComparisonNotificationContainer';
import { ComplaintModalContainer } from '../ComplaintModalContainer';
import { DealSaleLabelNewContainer } from '../DealSaleLabelContainer';
import { DepositPropertyLabelContainer } from '../DepositPropertyLabel';
import { DescriptionContainer } from '../Description/Description';
import { EarlyAccessLabelContainer } from '../EarlyAccessLabelContainer';
import { FavoritesFoldersContainer } from '../FavoritesFoldersContainer';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { HeatIndexContainer } from '../HeatIndexContainer';
import { IdenticalOffersContainer } from '../IdenticalOffersContainer';
import { MainNewContainer } from '../MainContainer';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { MortgageCalculatorContainer } from '../MortgageCalculator';
import { NavigationContainerNew } from '../NavigationNew';
import { NotAvailablePopupContainer } from '../NotAvailablePopupContainer';
import { ObjectFactoidsContainer } from '../ObjectFactoid/ObjectFactoid';
import { ObjectSummaryTitleContainer } from '../ObjectSummaryTitle/ObjectSummaryTitleContainer';
import { OfferMapContainer } from '../OfferMapContainer';
import { OfferMetaDataContainer } from '../OfferMetaData/OfferMetaData';
import { OfferSummaryInfoNewV2 } from '../OfferSummaryInfoNew';
import { OfferValuationContainer } from '../OfferValuation';
import { GoodPriceLabelNewContainer } from '../OfferValuation/containers/GoodPriceLabel';
import { OnlyCianLabelNewContainer } from '../OnlyCianLabelContainer';
import { PhoneWarningModalContainer } from '../PhoneWarningModalContainer';
import { RecommendedVillagesContainer } from '../RecommendedVillagesContainer';
import { RosReestrLabelContainer } from '../RosReestrLabelContainer';
import { RosreestrFlatSaleContainerNew } from '../RosreestrFlatSaleContainer';
import { SeoLinksContainer } from '../SeoLinks/SeoLinksContainer';
import { SimilarOffersGroupsContainer } from '../SimilarOffersRedesigned';
import { SubsidisedMortgageLabelContainer } from '../SubsidisedMortgageLabelContainer';
import { TourContainer } from '../TourContainer';
import { UxfbCookieUpdater } from '../UxfbCookieUpdater';
import { VisitedOffersUpdater } from '../VisitedOffersUpdater';
import { WarningsContainer } from '../WarningsContainer';
import { IsSoldFurnishedLabelContainer } from '../isSoldFurnishedLabelContainer';

const FlatPageContainer = () => {
  const context = useApplicationContext();
  const hiddenObjectsLkUrl = context.config.get<string>('hiddenObjects.lkUrl') || '';

  const offer = useSelector(getOffer);
  const isPublished = offer.status === 'published';

  const isHiddenByUser = useSelector((state: IState) => state.offerData.isHiddenByUser);
  const isTopPopupOpened = useSelector((state: IState) => state.hideOffer.isTopPopupOpened);
  const hideOfferStatus = useSelector((state: IState) => state.hideOffer.status);
  const hideOfferErrorMessage = useSelector((state: IState) => state.hideOffer.errorMessage);

  const dispatch = useDispatch();

  /**
   * Код появился к проекту "Чёрная пятница" для компании "Этажи".
   * @todo: удалить в задаче CD-251228
   */
  useGetEtazhiBlackFridayPriceData();

  useCardUxfeedbackCampaign();

  React.useEffect(() => {
    if (hasGetParameter('opendealrentform')) {
      dispatch(prefetchFormState());
    }
  }, []);

  return (
    <>
      <NavigationContainerNew />
      <OfferCardPageLayout>
        <FeedBackPopupC />
        <PopupFeedbackSentContainer />
        <FlatViewOrderTooltipContainer />
        <OfferCardPageLayoutTop>
          <OfferBreadcrumbsContainer />
          <OfferMetaDataContainer />
          <HeaderAdminPanelContainer />
          <OfferRefusedContainer config={context.config} />
          <OfferUnpublishedContainer />
          <AdminButtonsContainer />
          <ErrorLogComponent logger={context.logger}>
            <WarningsContainer />
          </ErrorLogComponent>
          {!isPublished && <SimilarOffersGroupsContainer />}
        </OfferCardPageLayoutTop>

        {isTopPopupOpened && (
          <HideOfferTopPopup
            status={hideOfferStatus}
            hidden={Boolean(isHiddenByUser)}
            lkUrl={hiddenObjectsLkUrl}
            hideOfferErrorMessage={hideOfferErrorMessage}
          />
        )}

        {isHiddenByUser && <OfferIsHiddenBlock lkUrl={hiddenObjectsLkUrl} />}

        <OfferCardPageLayoutCenter>
          <ErrorLogComponent logger={context.logger}>
            <HomeownerTrapContainer />
            <LabelsLayoutNew>
              <CpdLabelContainer />
              <SubsidisedMortgageLabelContainer />
              <EarlyAccessLabelContainer />
              <GoodPriceLabelNewContainer />
              <IsSoldFurnishedLabelContainer />
              <OnlyCianLabelNewContainer />
              <RosReestrLabelContainer />
              <DealSaleLabelNewContainer />
              <OnlineRentalAgreementLabelContainer />
              <AuctionLabelContainer />
              <DepositPropertyLabelContainer />
            </LabelsLayoutNew>
            <MainNewContainer />
          </ErrorLogComponent>
          {isPublished && (
            <div>
              <OfferGallery reactErrorLogger={context.logger} />
              <PrintPhotoContainer />
            </div>
          )}
          <ObjectSummaryTitleContainer />
          <ObjectFactoidsContainer />
          <DescriptionContainer />
          <CpdUnderDescriptionContainer />
          <CallUnderDescriptionContainer />
          <OfferSummaryInfoNewV2 />
          <RosreestrFlatSaleContainerNew />
          <CianLayoutContainer />
          <TourContainer />
          {isPublished && [
            <div key="published-data">
              <CardSectionNew>
                <ErrorLogComponent logger={context.logger}>
                  <OfferMapContainer />
                </ErrorLogComponent>
                <ErrorLogComponent logger={context.logger}>
                  <InfrastructureMicrofrontendContainer profile={EInfrastructureProfile.Urban} />
                </ErrorLogComponent>
                <ErrorLogComponent logger={context.logger}>
                  <RecommendedVillagesContainer />
                </ErrorLogComponent>
                <SecondaryMortgageCalculatorContainer />
                <MortgageCalculatorContainer />
              </CardSectionNew>
              <HeatIndexContainer />
              <OfferValuationContainer />
              <ValuationTrapLoader />
              <MortgageBrokerBannerContainer />
              <OfferHistoryContainer />
              <ErrorLogComponent logger={context.logger}>
                <CardSectionNew hideForPrint>
                  <BottomAuthorBlockContainer />
                </CardSectionNew>
              </ErrorLogComponent>
            </div>,
          ]}
        </OfferCardPageLayoutCenter>

        <OfferCardPageLayoutAside>
          <AsideRedesignedContainer />
        </OfferCardPageLayoutAside>
      </OfferCardPageLayout>
      <OfferCardPageLayoutFooter>
        <IdenticalOffersContainer />
        {isPublished && (
          <SimilarOffersAnchor>
            <SimilarOffersGroupsContainer />
          </SimilarOffersAnchor>
        )}
        <SeoLinksContainer />
        <TgbBottomBannersContainer />
      </OfferCardPageLayoutFooter>
      <PrintModalContainer />
      <PrintInformationContainer />
      <LegendModalContainer />
      <RemoveModalContainer />
      <ChatPopupContainer />
      <NotAvailablePopupContainer />
      <FavoritesFoldersContainer />
      <PhoneWarningModalContainer />
      <ComparisonNotificationLoadableContainer />
      <VisitedOffersUpdater />
      <UxfbCookieUpdater />
      <EarlyAccessPaywallContainer />
      <ComplaintModalContainer />
      <CpdLabelModalsContainer />
      <CpdPrelandingModal />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default FlatPageContainer;
